import React, { Dispatch, SetStateAction, useState, useEffect } from 'react';
import { ScoreComponent } from 'interfaces/organization';
import styles from '../admin-dashboard.module.scss';
import PinSpotIcon from '../../../assets/icons/pin_spot.svg';
import PersonIcon from '../../../assets/icons/person_gray.svg';
import { Organization } from '../../../graphql/codegen/schemas';
import { EXTERNAL_LINKS } from '../../../utils/constants';
import ViviHealthScoreGraph from './vivihealth-score-graph';
import ViviHealthMetricsModal from './vivihealth-metrics-modal';
import { getOrganizationScoreComponents } from '../../../services/api/organization';

type Props = {
  setShowEditModal: Dispatch<SetStateAction<boolean>>;
  organization: Organization;
};

const ClinicDetails: React.FC<Props> = ({ setShowEditModal, organization }) => {
  const [showMetricsModal, setShowMetricsModal] = useState<boolean>(false);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [sliderValues, setSliderValues] = useState<Record<string, number>>({});

  const openAddressOnGoogleMaps = () => {
    const addressURI = encodeURI(
      `${organization?.contact?.address?.line1}, ${organization?.contact?.address?.city}, ${organization?.contact?.address?.state}`,
    );
    const googleMapsURL = `${EXTERNAL_LINKS.GOOGLE_MAPS_QUERY}${addressURI}`;
    window.open(googleMapsURL);
  };

  const handleMetricsSave = (metrics: string[], newSliderValues: Record<string, number>) => {
    setSelectedMetrics(metrics);
    setSliderValues(newSliderValues);
    // Update the organization score components via API
    updateOrganizationScoreComponents(newSliderValues);
  };

  // Initialize slider values with equal distribution
  useEffect(() => {
    if (selectedMetrics.length > 0) {
      const equalValue = Math.floor(100 / selectedMetrics.length);
      const remainder = 100 - equalValue * selectedMetrics.length;

      const initialSliderValues: Record<string, number> = {};
      selectedMetrics.forEach((metric, index) => {
        initialSliderValues[metric] = equalValue + (index < remainder ? 1 : 0);
      });

      setSliderValues(initialSliderValues);
    }
  }, [selectedMetrics]);

  // Convert selected metrics and slider values to graph format
  const getGraphMetrics = () =>
    selectedMetrics.map(metric => ({
      name: metric,
      percentage: sliderValues[metric] || 0,
      color: '', // Color will be assigned by the graph component
    }));

  useEffect(() => {
    const getScoreComponents = async () => {
      const res = await getOrganizationScoreComponents(organization.id || '');
      if (res.status === 'Done') {
        const components: ScoreComponent[] = res.data?.components;
        const metricNames = components.map((component: ScoreComponent) => component.name);
        const metricValues: Record<string, number> = {};
        components.forEach(component => {
          metricValues[component.name] = component.weight;
        });
        setSelectedMetrics(metricNames);
        setSliderValues(metricValues);
      }
    };
    getScoreComponents();
  }, [organization.id]);

  const updateOrganizationScoreComponents = (metrics: Record<string, number>) => {
    setSliderValues(metrics);
  };

  return (
    <>
      <div className={styles.clinicDetails}>
        <div className={styles.topSection}>
          <span className={styles.header}>
            CLINIC DETAILS:
            <span
              className={styles.edit}
              onClick={() => setShowEditModal(true)}
              onKeyDown={e => e.key === 'Enter' && setShowEditModal(true)}
              role='button'
              tabIndex={0}
            >
              Edit
            </span>
          </span>
          <span className={styles.header}>
            VIVIHEALTH SCORE:
            <span
              className={styles.edit}
              onClick={() => setShowMetricsModal(true)}
              onKeyDown={e => e.key === 'Enter' && setShowMetricsModal(true)}
              role='button'
              tabIndex={0}
            >
              Edit
            </span>
          </span>
        </div>
        <div className={styles.body}>
          {/* Left side - Clinic and Practitioner Information */}
          <div className={styles.clinicInfoSection}>
            <div className={styles.infoRow}>
              <div className={styles.location}>
                <img src={PinSpotIcon} alt='Location pin icon' />
                <div className={styles.description}>
                  <span className={styles.name}>{organization.name}</span>
                  <span className={styles.phone}>{organization?.contact?.phone}</span>
                  <div
                    className={styles.address}
                    onClick={() => openAddressOnGoogleMaps()}
                    onKeyDown={e => e.key === 'Enter' && openAddressOnGoogleMaps()}
                    role='button'
                    tabIndex={0}
                  >
                    <span>{organization?.contact?.address?.line1},</span>
                    <span>{organization?.contact?.address?.line2},</span>
                    <span>{`${organization?.contact?.address?.city}, ${organization.contact?.address?.state} ${organization.contact?.address?.postalCode}`}</span>
                  </div>
                </div>
              </div>
              <div className={styles.contact}>
                <img src={PersonIcon} alt='Person contact icon' />
                <div className={styles.description}>
                  <span>{`${organization?.primaryContact?.name?.first} ${organization?.primaryContact?.name?.last}`}</span>
                  <span>{organization?.primaryContact?.phone}</span>
                  <span>{organization?.primaryContact?.email}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - ViviHealth Score */}
          <div className={styles.viviHealthSection}>
            <ViviHealthScoreGraph metrics={getGraphMetrics()} />
          </div>
        </div>
      </div>

      <ViviHealthMetricsModal
        show={showMetricsModal}
        hide={() => setShowMetricsModal(false)}
        selectedMetrics={sliderValues}
        onSave={handleMetricsSave}
        organizationId={organization.id || ''}
      />
    </>
  );
};

export default ClinicDetails;
